import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import HealthScoreCalculator from "./HealthScoreCalculator";

export default function HealthScoreDisplay({ investor, activities, tasks, onScoreUpdated }) {
  const score = investor.relationship_health_score;
  const lastUpdate = investor.last_score_update;

  const getScoreConfig = (score) => {
    if (score >= 80) {
      return {
        color: "text-emerald-600",
        bgColor: "bg-emerald-50",
        borderColor: "border-emerald-200",
        ringColor: "stroke-emerald-600",
        label: "Utmerket",
        icon: TrendingUp,
      };
    } else if (score >= 60) {
      return {
        color: "text-amber-600",
        bgColor: "bg-amber-50",
        borderColor: "border-amber-200",
        ringColor: "stroke-amber-600",
        label: "God",
        icon: Activity,
      };
    } else if (score >= 40) {
      return {
        color: "text-orange-600",
        bgColor: "bg-orange-50",
        borderColor: "border-orange-200",
        ringColor: "stroke-orange-600",
        label: "Moderat",
        icon: AlertTriangle,
      };
    } else {
      return {
        color: "text-red-600",
        bgColor: "bg-red-50",
        borderColor: "border-red-200",
        ringColor: "stroke-red-600",
        label: "Svak",
        icon: TrendingDown,
      };
    }
  };

  const config = score !== undefined && score !== null ? getScoreConfig(score) : null;
  const Icon = config?.icon;

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl ${config?.bgColor || "bg-slate-100"} flex items-center justify-center`}>
            {Icon ? <Icon className={`w-5 h-5 ${config.color}`} /> : <Activity className="w-5 h-5 text-slate-400" />}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Relasjonshelse</h3>
            <p className="text-sm text-slate-500">AI-drevet analyse</p>
          </div>
        </div>
        <HealthScoreCalculator 
          investor={investor} 
          activities={activities} 
          tasks={tasks}
          onScoreUpdated={onScoreUpdated}
        />
      </div>

      {score !== undefined && score !== null ? (
        <div className="space-y-4">
          {/* Score Circle */}
          <div className="flex items-center justify-center">
            <div className="relative w-40 h-40">
              <svg className="w-40 h-40 transform -rotate-90">
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  className="text-slate-200"
                />
                <circle
                  cx="80"
                  cy="80"
                  r="70"
                  stroke="currentColor"
                  strokeWidth="12"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 70}`}
                  strokeDashoffset={`${2 * Math.PI * 70 * (1 - score / 100)}`}
                  className={`${config.ringColor} transition-all duration-1000`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className={`text-4xl font-bold ${config.color}`}>{score}</span>
                <span className="text-sm text-slate-500">av 100</span>
              </div>
            </div>
          </div>

          {/* Status Badge */}
          <div className="flex justify-center">
            <Badge className={`${config.bgColor} ${config.color} border-0 px-4 py-1.5 text-sm font-semibold`}>
              {config.label} relasjon
            </Badge>
          </div>

          {/* Last Update */}
          {lastUpdate && (
            <p className="text-xs text-slate-500 text-center">
              Sist oppdatert: {format(new Date(lastUpdate), "d. MMM yyyy 'kl.' HH:mm", { locale: nb })}
            </p>
          )}

          {/* Score Breakdown Info */}
          <div className={`p-4 rounded-lg border ${config.borderColor} ${config.bgColor}`}>
            <p className="text-sm text-slate-700">
              {score >= 80 && "Relasjonen er i utmerket stand. Fortsett det gode arbeidet!"}
              {score >= 60 && score < 80 && "Relasjonen fungerer godt, men det er rom for forbedring."}
              {score >= 40 && score < 60 && "Relasjonen trenger oppmerksomhet. Vurder økt kommunikasjon."}
              {score < 40 && "Relasjonen krever umiddelbar oppfølging for å unngå tap."}
            </p>
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-4">
            Ingen helsecore beregnet ennå
          </p>
          <HealthScoreCalculator 
            investor={investor} 
            activities={activities} 
            tasks={tasks}
            onScoreUpdated={onScoreUpdated}
          />
        </div>
      )}
    </Card>
  );
}